import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  UserPlus,
  Send,
  Users,
  ChevronRight,
  RefreshCw,
  CheckCircle2,
  Circle,
  Sparkles,
  Radio,
  AlertTriangle,
  Pencil,
  Trash2,
  Save,
  ShieldCheck,
  FileText,
  Archive,
  Phone,
  MapPin,
  Star,
  ExternalLink,
  NotebookPen,
  IdCard,
  Map as MapIcon,
  LayoutList,
  Zap,
  Eye,
  GraduationCap,
  Camera,
  Plane,
  Briefcase,
  ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { fireBurst } from "@/lib/celebrate";
import ApertureLoader from "@/components/ApertureLoader";
import { StatusBadge, STATUS_LABELS } from "@/components/StatusBadge";
import { TierBadge, TIER_META, TIER_ORDER } from "@/components/TierBadge";
import { Checkbox } from "@/components/ui/checkbox";
import AttachmentPreviewModal from "@/components/AttachmentPreviewModal";
import ApplicantHeatmap from "@/components/applicants/ApplicantHeatmap";
import PhotographerTimeline from "@/components/photographer/PhotographerTimeline";

// Format byte counts as human-readable strings for attachment metadata.
const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const STATUS_ORDER = ["not_started", "in_progress", "completed", "approved"];

// Safe-URL guard — reject non-http(s) schemes (javascript:, data:, etc.) at
// render time. Server-side validation already blocks these on write, but
// legacy rows persisted before validation existed need defence-in-depth.
const safeHref = (u) => {
  if (!u || typeof u !== "string") return null;
  const s = u.trim();
  const low = s.toLowerCase();
  if (low.startsWith("http://") || low.startsWith("https://")) return s;
  return null;
};

function Metric({ label, value, accent = false }) {
  return (
    <div
      className={`card-surface rounded-sm p-5 ${accent ? "border-[#6B0F1A]/40" : ""}`}
      data-testid={`metric-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="font-mono-tag text-[10px] text-[#78584B]">{label}</div>
      <div
        className={`font-display text-4xl font-extrabold mt-2 tracking-tight ${
          accent ? "text-[#6B0F1A]" : "text-[#1A0F0A]"
        }`}
      >
        {value}
      </div>
    </div>
  );
}

/**
 * ApplicantFactSheet — reads-only summary of the info the applicant record
 * carried onto this photographer's User doc at invite time. Renders nothing
 * when every field is blank (i.e. photographers seeded manually without ever
 * going through the applicant pipeline) so the drawer stays tight for them.
 */
/**
 * ApplicantFactSheet — reads-only summary of the info the applicant record
 * carried onto this photographer's User doc at invite time, with an inline
 * edit mode so admins can tweak phone / notes / resume / score without
 * leaving the roster drawer. Renders a compact "no file on record" ghost
 * (with an ADD button) when every field is blank so photographers seeded
 * manually still get a way in.
 */
// Inline role badge for the roster table. Kept private to this file
// because the Applicants version uses `applied_role` (only PHOTOG/PILOT) —
// here we key on the User's `role` and include Employee.
const ROSTER_ROLE_META = {
  photographer: { label: "Photo",     icon: Camera,    classes: "border-[#6B0F1A]/30 bg-[#6B0F1A]/8 text-[#6B0F1A]" },
  pilot:        { label: "Pilot",      icon: Plane,     classes: "border-[#3F7A2E]/40 bg-[#3F7A2E]/10 text-[#3F7A2E]" },
  employee:     { label: "Employee",   icon: Briefcase, classes: "border-[#B08D57]/50 bg-[#B08D57]/12 text-[#8A6D3A]" },
};

function RosterRoleBadge({ role, testId }) {
  const meta = ROSTER_ROLE_META[role] || ROSTER_ROLE_META.photographer;
  const Icon = meta.icon;
  return (
    <span
      data-testid={testId}
      title={meta.label}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm border font-mono-tag text-[9px] tracking-[0.14em] ${meta.classes}`}
    >
      <Icon className="w-2.5 h-2.5" strokeWidth={2} />
      {meta.label.toUpperCase()}
    </span>
  );
}


function ApplicantFactSheet({ user, onSave }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(null);

  const openEdit = () => {
    setDraft({
      phone: user?.phone || "",
      notes: user?.notes || "",
      resume_url: user?.resume_url || "",
      score: user?.score || 0,
    });
    setEditing(true);
  };
  const cancelEdit = () => { setEditing(false); setDraft(null); };
  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      await onSave({
        phone: draft.phone.trim(),
        notes: draft.notes.trim(),
        resume_url: draft.resume_url.trim(),
        score: Number(draft.score) || 0,
      });
      toast.success("Applicant file updated.");
      setEditing(false);
      setDraft(null);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not save.";
      toast.error(typeof msg === "string" ? msg : "Could not save.");
    } finally { setSaving(false); }
  };

  const stars = user?.score || 0;
  const city = user?.resolved_city || user?.desired_location;
  const zip = user?.base_zip_code;
  const region = user?.region;
  const phone = user?.phone;
  const travel = user?.desired_travel_distance;
  const notes = user?.notes;
  const resume = safeHref(user?.resume_url);

  // Ghost-empty state — no applicant history, but still expose an ADD entry
  // so admins can start attaching notes/score directly from the drawer.
  const empty =
    !stars && !city && !zip && !region && !phone && !travel && !notes && !resume;
  if (empty && !editing) {
    return (
      <div
        className="mt-6 card-surface rounded-sm p-4 flex items-center justify-between gap-3"
        data-testid="applicant-fact-sheet-empty"
      >
        <div className="flex items-center gap-2 text-xs text-[#78584B]">
          <IdCard className="w-3.5 h-3.5" strokeWidth={2} />
          No applicant file on record for this photographer.
        </div>
        <Button
          onClick={openEdit}
          data-testid="fact-sheet-add-btn"
          variant="ghost"
          size="sm"
          className="text-[#6B0F1A] hover:bg-[#6B0F1A]/8 font-mono-tag text-[10px] border border-[#6B0F1A]/30"
        >
          <Pencil className="w-3 h-3 mr-1" strokeWidth={2} /> ADD
        </Button>
      </div>
    );
  }

  return (
    <div
      className="mt-6 card-surface rounded-sm p-5 space-y-3"
      data-testid="applicant-fact-sheet"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <IdCard className="w-4 h-4 text-[#B08D57]" strokeWidth={1.75} />
          <div className="font-mono-tag text-[10px] text-[#78584B] tracking-wider">
            APPLICANT FILE · CARRIED OVER AT INVITE
          </div>
        </div>
        {editing ? (
          <div className="flex items-center gap-1">
            <Button
              onClick={cancelEdit}
              disabled={saving}
              data-testid="fact-sheet-cancel-btn"
              variant="ghost"
              size="sm"
              className="text-[#78584B] hover:bg-[#1A0F0A]/5 font-mono-tag text-[10px] h-7 px-2"
            >
              CANCEL
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              data-testid="fact-sheet-save-btn"
              size="sm"
              className="bg-[#6B0F1A] hover:bg-[#8A1424] text-[#F5EBD9] font-mono-tag text-[10px] h-7 px-2.5"
            >
              <Save className="w-3 h-3 mr-1" strokeWidth={2} />
              {saving ? "SAVING…" : "SAVE"}
            </Button>
          </div>
        ) : (
          <Button
            onClick={openEdit}
            data-testid="fact-sheet-edit-btn"
            variant="ghost"
            size="sm"
            className="text-[#4D3F35] hover:bg-[#1A0F0A]/5 font-mono-tag text-[10px] h-7 px-2 border border-[#1A0F0A]/12"
          >
            <Pencil className="w-3 h-3 mr-1" strokeWidth={2} /> EDIT
          </Button>
        )}
      </div>

      {/* Score row — always visible so admins can grade any moment. */}
      <div className="flex items-center justify-between gap-2 pb-1">
        <div className="font-mono-tag text-[10px] text-[#78584B] tracking-wider">SCORE</div>
        <StarPicker
          value={editing ? draft.score : stars}
          onChange={editing ? (n) => setDraft((d) => ({ ...d, score: n })) : undefined}
          readOnly={!editing}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-4 text-sm">
        {(editing || phone) && (
          <FactRow icon={Phone} label="PHONE" testId="fact-sheet-phone">
            {editing ? (
              <Input
                value={draft.phone}
                onChange={(e) => setDraft((d) => ({ ...d, phone: e.target.value }))}
                placeholder="+1 555 123 4567"
                data-testid="fact-sheet-phone-input"
                className="h-7 mt-1 bg-white border-[#1A0F0A]/15 rounded-sm text-xs"
              />
            ) : (
              <a
                href={`tel:${phone.replace(/[^+\d]/g, "")}`}
                className="text-[#1A0F0A] hover:text-[#6B0F1A] font-mono-tag text-xs"
              >
                {phone}
              </a>
            )}
          </FactRow>
        )}
        {(city || region || zip) && (
          <FactRow icon={MapPin} label="LOCATION" testId="fact-sheet-location">
            <span className="text-[#1A0F0A]">
              {city || region || "—"}
              {zip && (
                <span className="font-mono-tag text-[10px] text-[#78584B] ml-1.5">
                  · {zip}
                </span>
              )}
            </span>
          </FactRow>
        )}
        {travel && (
          <FactRow icon={RefreshCw} label="WILL TRAVEL" testId="fact-sheet-travel">
            <span className="text-[#1A0F0A]">{travel}</span>
          </FactRow>
        )}
        {(editing || resume) && (
          <FactRow icon={FileText} label="RESUME URL" testId="fact-sheet-resume">
            {editing ? (
              <Input
                value={draft.resume_url}
                onChange={(e) => setDraft((d) => ({ ...d, resume_url: e.target.value }))}
                placeholder="https://…/resume.pdf"
                data-testid="fact-sheet-resume-input"
                className="h-7 mt-1 bg-white border-[#1A0F0A]/15 rounded-sm text-xs"
              />
            ) : (
              <a
                href={safeHref(resume) || "#"}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[#6B0F1A] hover:text-[#8A1424] font-bold text-xs tracking-wide"
              >
                Open <ExternalLink className="w-3 h-3" strokeWidth={2} />
              </a>
            )}
          </FactRow>
        )}
      </div>

      {(editing || notes) && (
        <div className="pt-3 border-t border-[#1A0F0A]/10" data-testid="fact-sheet-notes">
          <div className="inline-flex items-center gap-1.5 font-mono-tag text-[10px] text-[#78584B] tracking-wider mb-1.5">
            <NotebookPen className="w-3 h-3" strokeWidth={2} /> APPLICANT NOTES
          </div>
          {editing ? (
            <Textarea
              value={draft.notes}
              onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
              rows={4}
              placeholder="Any info the pipeline caught that the photographer should never re-enter…"
              data-testid="fact-sheet-notes-input"
              className="w-full bg-white border-[#1A0F0A]/15 rounded-sm text-[13px] focus-visible:border-[#6B0F1A] focus-visible:ring-[#6B0F1A]/25"
            />
          ) : (
            <p className="text-[13px] text-[#1A0F0A] leading-relaxed whitespace-pre-wrap">
              {notes}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/** Compact 5-star picker — read-only in view mode, clickable in edit mode.
 *  Click the current highest star again to clear the score to zero. */
function StarPicker({ value, onChange, readOnly }) {
  return (
    <div
      className="inline-flex items-center"
      data-testid="fact-sheet-score"
      data-value={value || 0}
    >
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          disabled={readOnly}
          onClick={readOnly ? undefined : () => onChange(n === value ? 0 : n)}
          data-testid={`fact-sheet-star-${n}`}
          className={`w-4 h-5 grid place-items-center rounded-sm ${
            readOnly ? "cursor-default" : "hover:bg-[#B08D57]/10 cursor-pointer"
          }`}
        >
          <Star
            className={`w-4 h-4 ${
              n <= (value || 0) ? "fill-[#B08D57] text-[#B08D57]" : "text-[#1A0F0A]/20"
            }`}
            strokeWidth={1.75}
          />
        </button>
      ))}
    </div>
  );
}

function FactRow({ icon: Icon, label, testId, children }) {
  return (
    <div className="flex items-start gap-2 min-w-0" data-testid={testId}>
      <Icon className="w-3.5 h-3.5 text-[#78584B] mt-0.5 shrink-0" strokeWidth={2} />
      <div className="min-w-0 flex-1">
        <div className="font-mono-tag text-[9px] text-[#78584B] tracking-wider">{label}</div>
        <div className="truncate">{children}</div>
      </div>
    </div>
  );
}

/**
 * RosterMapView — swaps the roster table for a geographic heatmap of every
 * approved photographer. Reuses the same `ApplicantHeatmap` component that
 * powers the applicant pipeline map (via `entity="photographer"`), keeping
 * one source of truth for map filters, popups, hover states, ranking lists,
 * and gap detection. Data is fetched lazily the first time the admin flips
 * to map view and then cached in parent state so re-toggling is instant.
 */
function RosterMapView({ mapData, setMapData, mapLoading, setMapLoading, statusFilter, roleFilter }) {
  useEffect(() => {
    if (mapData || mapLoading) return;
    (async () => {
      setMapLoading(true);
      try {
        const { data } = await api.get("/admin/roster-map");
        setMapData(data);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Failed to load roster map.");
      } finally {
        setMapLoading(false);
      }
    })();
  }, [mapData, mapLoading, setMapData, setMapLoading]);

  // Every stage the API returned is on by default; the heatmap's own filter
  // chips let the admin narrow it further. When the roster STATUS or ROLE
  // filter is narrowed (e.g. "approved" only, or "pilot" only), we mirror
  // it here by filtering the photographer list before handing it to the
  // heatmap.
  const filteredPhotographers = useMemo(() => {
    if (!mapData?.photographers) return [];
    return mapData.photographers.filter((p) => {
      const s = p.status || p.user?.status;
      const r = p.role || p.user?.role || "photographer";
      const statusOk = statusFilter === "all" || !s || s === statusFilter;
      const roleOk   = roleFilter   === "all" || r === roleFilter;
      return statusOk && roleOk;
    });
  }, [mapData, statusFilter, roleFilter]);

  const activeStages = useMemo(
    () => new Set(mapData?.stages || []),
    [mapData?.stages],
  );

  if (mapLoading || !mapData) {
    return (
      <div className="py-16" data-testid="roster-map-loading">
        <ApertureLoader label="Loading roster map…" />
      </div>
    );
  }

  return (
    <div data-testid="roster-map-view">
      <ApplicantHeatmap
        applicants={filteredPhotographers}
        activeStages={activeStages}
        entity="photographer"
      />
    </div>
  );
}

export default function AdminDashboard() {
  const { user: currentUser } = useAuth();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [selectedHandoff, setSelectedHandoff] = useState(null);
  const [selectedTimeline, setSelectedTimeline] = useState(null);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [retryingHandoff, setRetryingHandoff] = useState(false);
  // ----- Hub connection diagnostics (Test connection + Preview payload) -----
  // These live alongside the per-photographer handoff card but exercise the
  // Hub webhook globally — no photographer is affected. Result is stored so
  // admins can leave the drawer open and re-run without state loss.
  const [hubTesting, setHubTesting] = useState(false);
  const [hubTestResult, setHubTestResult] = useState(null);
  const [hubPreview, setHubPreview] = useState(null);
  const [hubPreviewLoading, setHubPreviewLoading] = useState(false);
  const [hubPreviewOpen, setHubPreviewOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [editForm, setEditForm] = useState({ name: "", base_zip_code: "", driving_radius_miles: "" });
  const [savingProfile, setSavingProfile] = useState(false);
  const [togglingItem, setTogglingItem] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  // Which onboarding role the invitee should land as. Photographer is the
  // default so admins can send single-key invites like before; the role
  // selector lets them promote a Pilot / Employee without opening the
  // Applicants flow.
  const [inviteRole, setInviteRole] = useState("photographer");
  const [inviting, setInviting] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  // Role filter for the roster page — All / Photographer / Pilot / Employee.
  // Persisted only in-memory. Applies to both list AND map views (see
  // `filteredForMap` below), so switching to the Map tab keeps the current
  // role slice.
  const [roleFilter, setRoleFilter] = useState("all");
  const [savingStatus, setSavingStatus] = useState(false);
  // ----- View mode toggle: list ↔ map -----
  // Map data is fetched LAZILY the first time an admin flips to "map" so we
  // don't pay for the ZIP-geocoded roster payload for admins who only ever
  // use the list view. Cached after first load; both views share filters.
  const [viewMode, setViewMode] = useState("list");
  const [mapData, setMapData] = useState(null);
  const [mapLoading, setMapLoading] = useState(false);
  // ----- Admin management -----
  const [adminsOpen, setAdminsOpen] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [adminsLoading, setAdminsLoading] = useState(false);
  const [adminInviteEmail, setAdminInviteEmail] = useState("");
  const [adminInviteName, setAdminInviteName] = useState("");
  const [invitingAdmin, setInvitingAdmin] = useState(false);
  const [revokingAdminId, setRevokingAdminId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/overview");
      setOverview(data);
    } catch (e) {
      toast.error("Could not load admin overview.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const loadDetail = async (userId) => {
    setSelectedDetail(null);
    setSelectedHandoff(null);
    setSelectedTimeline(null);
    try {
      const [{ data }, handoff, timeline] = await Promise.all([
        api.get(`/admin/photographers/${userId}`),
        api.get(`/admin/photographers/${userId}/handoff`).then((r) => r.data).catch(() => null),
        api.get(`/admin/photographers/${userId}/timeline`).then((r) => r.data?.milestones || []).catch(() => []),
      ]);
      setSelectedDetail(data);
      setSelectedHandoff(handoff);
      setSelectedTimeline(timeline);
    } catch (e) { toast.error("Could not load photographer details."); }
  };

  const retryHandoff = async () => {
    if (!selectedDetail) return;
    setRetryingHandoff(true);
    try {
      const { data } = await api.post(`/admin/photographers/${selectedDetail.user.id}/handoff/retry`);
      setSelectedHandoff(data);
      if (data.status === "success") toast.success("Handoff sent to Shoots Hub.");
      else if (data.status === "pending_config") toast.info("Hub webhook URL not configured — set SHOOTS_HUB_WEBHOOK_URL.");
      else toast.error(data.error || "Handoff failed.");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not send handoff.";
      toast.error(typeof msg === "string" ? msg : "Could not send handoff.");
    } finally { setRetryingHandoff(false); }
  };

  // Fire a signed `photographer.test` ping at the configured Shoots Hub URL
  // so an admin can verify URL + Bearer + HMAC-SHA256 signature end-to-end
  // without approving a real photographer.
  const testHubConnection = async () => {
    setHubTesting(true);
    try {
      const { data } = await api.post("/admin/hub/test-connection");
      setHubTestResult(data);
      if (data.status === "success") toast.success(`Shoots Hub responded HTTP ${data.response_code}.`);
      else if (data.status === "pending_config") toast.info("Hub webhook URL not configured.");
      else toast.error(data.error || `Hub test failed${data.response_code ? ` (HTTP ${data.response_code})` : ""}.`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not reach the test-connection endpoint.";
      toast.error(typeof msg === "string" ? msg : "Test failed.");
    } finally { setHubTesting(false); }
  };

  // Fetch (but do NOT send) the exact payload + signed headers the Hub would
  // receive. Toggling the panel while it's open closes it.
  const loadHubPreview = async () => {
    if (hubPreviewOpen) { setHubPreviewOpen(false); return; }
    setHubPreviewLoading(true);
    try {
      const { data } = await api.get("/admin/hub/payload-preview");
      setHubPreview(data);
      setHubPreviewOpen(true);
    } catch (e) {
      toast.error("Could not load the payload preview.");
    } finally { setHubPreviewLoading(false); }
  };

  // Bundle every document a photographer has uploaded into a single .zip and
  // trigger a browser download. Called from both the roster row and the detail
  // sheet. Shows a specific toast when the photographer has no uploads yet.
  const downloadAllDocs = async (userId, displayName) => {
    try {
      const resp = await api.get(
        `/admin/photographers/${userId}/attachments/zip`,
        { responseType: "blob" }
      );
      const blob = new Blob([resp.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const disposition = resp.headers?.["content-disposition"] || "";
      const match = disposition.match(/filename="?([^";]+)"?/i);
      const fname = match ? match[1] : `reelty-${(displayName || "photographer").replace(/\s+/g, "_")}-documents.zip`;
      const a = document.createElement("a");
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      toast.success(`Downloaded documents for ${displayName || "photographer"}.`);
    } catch (e) {
      // Blob-typed 404s hide the JSON body — parse it out.
      let detail = null;
      const data = e?.response?.data;
      if (data instanceof Blob) {
        try {
          const txt = await data.text();
          const parsed = JSON.parse(txt);
          detail = parsed?.detail;
        } catch { /* ignore */ }
      } else {
        detail = data?.detail;
      }
      toast.error(typeof detail === "string" ? detail : "Could not download documents.");
    }
  };

  const openEditProfile = () => {
    if (!selectedDetail) return;
    setEditForm({
      name: selectedDetail.user.name || "",
      base_zip_code: selectedDetail.user.base_zip_code || "",
      driving_radius_miles: selectedDetail.user.driving_radius_miles ? String(selectedDetail.user.driving_radius_miles) : "",
    });
    setEditingProfile(true);
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    if (!selectedDetail) return;
    setSavingProfile(true);
    try {
      const payload = {
        name: editForm.name.trim() || null,
        base_zip_code: editForm.base_zip_code.trim() || null,
        driving_radius_miles: editForm.driving_radius_miles ? parseInt(editForm.driving_radius_miles, 10) : null,
      };
      const { data } = await api.patch(`/admin/photographers/${selectedDetail.user.id}/profile`, payload);
      toast.success("Profile updated.");
      setSelectedDetail({ ...selectedDetail, user: data });
      setOverview((prev) => prev ? {
        ...prev,
        photographers: prev.photographers.map((row) => row.user.id === data.id ? { ...row, user: data } : row),
      } : prev);
      setEditingProfile(false);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not save profile.";
      toast.error(typeof msg === "string" ? msg : "Could not save profile.");
    } finally { setSavingProfile(false); }
  };

  // Fact-sheet in-place save — takes just the diff (phone / notes / resume /
  // score) and reuses the same admin profile endpoint. Cascades state
  // updates back into the drawer + the roster row so the changes appear
  // everywhere the user is rendered without a full reload.
  const saveFactSheet = async (patch) => {
    if (!selectedDetail) return;
    const { data } = await api.patch(
      `/admin/photographers/${selectedDetail.user.id}/profile`,
      patch,
    );
    setSelectedDetail((cur) => (cur ? { ...cur, user: data } : cur));
    setOverview((prev) => prev ? {
      ...prev,
      photographers: prev.photographers.map((row) =>
        row.user.id === data.id ? { ...row, user: data } : row,
      ),
    } : prev);
    return data;
  };

  const toggleAdminItem = async (itemId, completed) => {
    if (!selectedDetail) return;
    setTogglingItem(itemId);
    // optimistic
    setSelectedDetail((prev) => prev ? {
      ...prev,
      items: prev.items.map((i) => i.id === itemId ? { ...i, completed } : i),
    } : prev);
    try {
      await api.post(`/admin/photographers/${selectedDetail.user.id}/checklist/${itemId}/toggle`, { completed });
      // Refresh authoritative counts
      const { data } = await api.get(`/admin/photographers/${selectedDetail.user.id}`);
      setSelectedDetail(data);
      setOverview((prev) => prev ? {
        ...prev,
        photographers: prev.photographers.map((row) => row.user.id === data.user.id ? {
          ...row,
          user: data.user,
          completed_items: data.completed_items,
          percent_complete: data.percent_complete,
          service_area_complete: data.service_area_complete,
        } : row),
      } : prev);
    } catch {
      toast.error("Could not update item.");
      loadDetail(selectedDetail.user.id);
    } finally { setTogglingItem(null); }
  };

  // Fetch a photographer's uploaded attachment through the authorized API and open it
  // in a new tab (or force-download) via a blob URL. Blob URL is revoked after ~60s.
  const openAdminAttachment = async (userId, itemId, fileId, forceDownload = false) => {
    try {
      const resp = await api.get(
        `/admin/photographers/${userId}/checklist/${itemId}/attachment/${fileId}/download`,
        { params: { force: forceDownload }, responseType: "blob" }
      );
      const blob = new Blob([resp.data], { type: resp.data.type || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      if (forceDownload) {
        const disposition = resp.headers?.["content-disposition"] || "";
        const match = disposition.match(/filename="?([^";]+)"?/i);
        const fname = match ? match[1] : "attachment";
        const a = document.createElement("a");
        a.href = url;
        a.download = fname;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        window.open(url, "_blank", "noopener,noreferrer");
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error("Could not open attachment.");
    }
  };

  const deletePhotographer = async () => {
    if (!selectedDetail) return;
    const name = selectedDetail.user.name || selectedDetail.user.email;
    if (!window.confirm(`Delete ${name}? This removes all their onboarding progress and cannot be undone.`)) return;
    try {
      await api.delete(`/admin/photographers/${selectedDetail.user.id}`);
      toast.success(`${name} deleted.`);
      setSelectedId(null);
      setSelectedDetail(null);
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not delete photographer.";
      toast.error(typeof msg === "string" ? msg : "Could not delete photographer.");
    }
  };

  const invite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      await api.post("/admin/photographers/invite", {
        email: inviteEmail.trim().toLowerCase(),
        name: inviteName.trim() || null,
        role: inviteRole,
      });
      toast.success(
        `${inviteRole === "pilot" ? "Pilot" : inviteRole === "employee" ? "Employee" : "Photographer"} invite sent to ${inviteEmail}`,
      );
      setInviteEmail(""); setInviteName(""); setInviteRole("photographer"); setInviteOpen(false);
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not invite photographer.";
      toast.error(typeof msg === "string" ? msg : "Could not invite photographer.");
    } finally {
      setInviting(false);
    }
  };

  const updateStatus = async (userId, newStatus) => {
    setSavingStatus(true);
    try {
      const { data } = await api.patch(`/admin/photographers/${userId}/status`, { status: newStatus });
      toast.success(`Status updated to ${STATUS_LABELS[newStatus].label}`);
      setOverview((prev) => prev ? {
        ...prev,
        photographers: prev.photographers.map((row) => row.user.id === userId ? { ...row, user: data } : row),
      } : prev);
      if (selectedDetail?.user?.id === userId) setSelectedDetail({ ...selectedDetail, user: data });
    } catch (e) { toast.error("Could not update status."); }
    finally { setSavingStatus(false); }
  };

  const resendInvite = async (userId) => {
    try {
      await api.post(`/admin/photographers/${userId}/resend-invite`);
      toast.success("Invite email resent.");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not resend invite.";
      toast.error(typeof msg === "string" ? msg : "Could not resend invite.");
    }
  };

  // ---------- Admin management ----------
  const loadAdmins = async () => {
    setAdminsLoading(true);
    try {
      const { data } = await api.get("/admin/admins");
      setAdmins(data);
    } catch (e) {
      toast.error("Could not load admins.");
    } finally {
      setAdminsLoading(false);
    }
  };

  const openAdminsDialog = () => {
    setAdminsOpen(true);
    loadAdmins();
  };

  const inviteAdmin = async (e) => {
    e.preventDefault();
    const email = adminInviteEmail.trim().toLowerCase();
    if (!email) return;
    setInvitingAdmin(true);
    try {
      await api.post("/admin/admins/invite", {
        email,
        name: adminInviteName.trim() || null,
      });
      toast.success(`Admin invite sent to ${email}`);
      setAdminInviteEmail("");
      setAdminInviteName("");
      await loadAdmins();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not invite admin.";
      toast.error(typeof msg === "string" ? msg : "Could not invite admin.");
    } finally {
      setInvitingAdmin(false);
    }
  };

  const resendAdminInvite = async (userId) => {
    try {
      await api.post(`/admin/admins/${userId}/resend-invite`);
      toast.success("Admin magic link resent.");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not resend admin invite.";
      toast.error(typeof msg === "string" ? msg : "Could not resend admin invite.");
    }
  };

  const revokeAdmin = async (userId, email) => {
    if (!window.confirm(`Revoke admin access for ${email}? They will no longer be able to sign in as an admin.`)) return;
    setRevokingAdminId(userId);
    try {
      await api.delete(`/admin/admins/${userId}`);
      toast.success(`${email} is no longer an admin.`);
      await loadAdmins();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not revoke admin.";
      toast.error(typeof msg === "string" ? msg : "Could not revoke admin.");
    } finally {
      setRevokingAdminId(null);
    }
  };

  const updateTier = async (userId, newTier) => {
    const payload = { tier: newTier === "unassigned" ? null : newTier };
    try {
      const { data } = await api.patch(`/admin/photographers/${userId}/tier`, payload);
      toast.success(
        payload.tier
          ? `Tier set to ${TIER_META[payload.tier].label} · $${TIER_META[payload.tier].rate}/shoot`
          : "Tier cleared"
      );
      setOverview((prev) => prev ? {
        ...prev,
        photographers: prev.photographers.map((row) => row.user.id === userId ? { ...row, user: data } : row),
      } : prev);
      if (selectedDetail?.user?.id === userId) setSelectedDetail({ ...selectedDetail, user: data });
    } catch (e) { toast.error("Could not update tier."); }
  };

  // Flip the "does their own editing" flag on a photographer / pilot /
  // employee. Optimistic update keeps the drawer + roster row snappy —
  // any failure toasts and refetches implicitly by leaving the previous
  // state in place (backend is the source of truth on the next full
  // overview load).
  const toggleCanEdit = async (userId, next) => {
    try {
      const { data } = await api.patch(`/admin/photographers/${userId}/can-edit`, { can_edit: next });
      toast.success(next ? "Editing enabled — this user delivers final files." : "Editing disabled — Reelty handles post.");
      setOverview((prev) => prev ? {
        ...prev,
        photographers: prev.photographers.map((row) => row.user.id === userId ? { ...row, user: data } : row),
      } : prev);
      if (selectedDetail?.user?.id === userId) setSelectedDetail({ ...selectedDetail, user: data });
    } catch (e) { toast.error("Could not update editing capability."); }
  };

  const filtered = useMemo(() => {
    if (!overview) return [];
    let rows = overview.photographers;
    if (statusFilter !== "all") rows = rows.filter((r) => r.user.status === statusFilter);
    if (roleFilter !== "all") rows = rows.filter((r) => (r.user.role || "photographer") === roleFilter);
    return rows;
  }, [overview, statusFilter, roleFilter]);

  // LIVE counts — recomputed from the current photographers array so the metric
  // cards always reflect the latest status, even after inline edits in the sheet.
  // Only counts users matching the active ROLE filter so per-role totals stay honest.
  const liveCounts = useMemo(() => {
    const c = { total: 0, not_started: 0, in_progress: 0, completed: 0, approved: 0 };
    if (!overview) return c;
    for (const row of overview.photographers) {
      if (roleFilter !== "all" && (row.user.role || "photographer") !== roleFilter) continue;
      c.total += 1;
      c[row.user.status] = (c[row.user.status] || 0) + 1;
    }
    return c;
  }, [overview, roleFilter]);

  // Per-role totals (independent of statusFilter) so the role tabs can
  // always show accurate `TOTAL n` chips even when the user has narrowed
  // by status.
  const roleTotals = useMemo(() => {
    const t = { all: 0, photographer: 0, pilot: 0, employee: 0 };
    if (!overview) return t;
    for (const row of overview.photographers) {
      const r = row.user.role || "photographer";
      t.all += 1;
      t[r] = (t[r] || 0) + 1;
    }
    return t;
  }, [overview]);

  if (loading) {
    return <ApertureLoader label="Loading roster…" />;
  }

  if (!overview) return null;

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8 md:py-10" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
        <div className="min-w-0 flex-1">
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tighter leading-[1.05] text-[#1A0F0A]">
            Roster & Onboarding
          </h1>
          <p className="text-sm md:text-base text-[#4D3F35] mt-3 max-w-xl">
            Invite photographers, monitor onboarding progress, assign per-shoot tiers,
            and approve them for residential property assignments.
          </p>
        </div>

        {/* Action buttons — stack full-width on mobile so the three CTAs don't
            cram into a 390px row underneath the title. `w-full sm:w-auto` +
            `flex-1` per button gives a clean stacked look on phones and the
            original inline layout from `sm:` and up. */}
        <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
          <Button
            onClick={openAdminsDialog}
            data-testid="manage-admins-btn"
            variant="ghost"
            className="border border-[#1A0F0A]/15 hover:bg-[#1A0F0A]/5 text-[#1A0F0A] font-bold tracking-wide rounded-sm flex-1 sm:flex-none min-w-0"
          >
            <ShieldCheck className="w-4 h-4 mr-2 shrink-0" strokeWidth={2} />
            <span>Admins</span>
          </Button>

          <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="invite-photographer-btn"
              className="bg-[#6B0F1A] hover:bg-[#8A1424] text-[#F5EBD9] font-bold tracking-wide rounded-sm btn-primary-glow w-full sm:w-auto"
            >
              <UserPlus className="w-4 h-4 mr-2 shrink-0" strokeWidth={2} />
              <span className="hidden xs:inline sm:inline">Invite User</span>
              <span className="inline xs:hidden sm:hidden">Invite</span>
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#FBF4E4] border-[#1A0F0A]/12 text-[#1A0F0A] rounded-sm" data-testid="invite-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl tracking-tight text-[#1A0F0A]">
                Invite a {inviteRole === "pilot" ? "pilot" : inviteRole === "employee" ? "Reelty employee" : "photographer"}
              </DialogTitle>
              <DialogDescription className="text-[#4D3F35]">
                They'll receive a magic-link email to start onboarding on their role's checklist.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={invite} className="space-y-4 mt-2" data-testid="invite-form">
              {/* Role picker — 3-way segmented control. Same set as the
                  Roster tabs (Photographer / Pilot / Employee). Toggling
                  updates dialog copy + success toast copy above. */}
              <div>
                <Label className="font-mono-tag text-[10px] text-[#78584B]">ROLE</Label>
                <div
                  className="mt-1.5 grid grid-cols-3 gap-2"
                  role="radiogroup"
                  aria-label="Onboarding role"
                  data-testid="invite-role-picker"
                >
                  {[
                    { key: "photographer", label: "Photographer", icon: Camera },
                    { key: "pilot",        label: "Pilot",        icon: Plane },
                    { key: "employee",     label: "Employee",     icon: Briefcase },
                  ].map((r) => {
                    const selected = inviteRole === r.key;
                    const Icon = r.icon;
                    return (
                      <button
                        key={r.key}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        onClick={() => setInviteRole(r.key)}
                        data-testid={`invite-role-${r.key}`}
                        className={`inline-flex items-center justify-center gap-1.5 px-2 py-2 rounded-sm border text-xs font-bold tracking-tight transition-all ${
                          selected
                            ? "border-[#6B0F1A] bg-[#6B0F1A]/8 text-[#6B0F1A]"
                            : "border-[#1A0F0A]/15 bg-white text-[#1A0F0A] hover:border-[#1A0F0A]/35"
                        }`}
                      >
                        <Icon
                          className={`w-3.5 h-3.5 ${selected ? "text-[#6B0F1A]" : "text-[#78584B]"}`}
                          strokeWidth={2}
                        />
                        {r.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label htmlFor="invite-email" className="font-mono-tag text-[10px] text-[#78584B]">EMAIL</Label>
                <Input
                  id="invite-email"
                  data-testid="invite-email-input"
                  type="email"
                  required
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder={
                    inviteRole === "pilot"
                      ? "pilot@studio.com"
                      : inviteRole === "employee"
                      ? "teammate@goreelty.com"
                      : "photographer@studio.com"
                  }
                  className="mt-1.5 bg-white border-[#1A0F0A]/15 focus-visible:border-[#6B0F1A] focus-visible:ring-[#6B0F1A]/25 rounded-sm text-[#1A0F0A]"
                />
              </div>
              <div>
                <Label htmlFor="invite-name" className="font-mono-tag text-[10px] text-[#78584B]">NAME (OPTIONAL)</Label>
                <Input
                  id="invite-name"
                  data-testid="invite-name-input"
                  value={inviteName}
                  onChange={(e) => setInviteName(e.target.value)}
                  placeholder="Alex Rivera"
                  className="mt-1.5 bg-white border-[#1A0F0A]/15 focus-visible:border-[#6B0F1A] focus-visible:ring-[#6B0F1A]/25 rounded-sm text-[#1A0F0A]"
                />
              </div>
              <DialogFooter className="mt-6">
                <Button
                  type="submit"
                  data-testid="invite-send-btn"
                  disabled={inviting}
                  className="bg-[#6B0F1A] hover:bg-[#8A1424] text-[#F5EBD9] font-bold tracking-wide rounded-sm w-full"
                >
                  <Send className="w-4 h-4 mr-2" strokeWidth={2} />
                  {inviting ? "Sending…" : "Send invite"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
        <Metric label="Total" value={liveCounts.total} accent />
        <Metric label="Not Started" value={liveCounts.not_started} />
        <Metric label="In Progress" value={liveCounts.in_progress} />
        <Metric label="Completed" value={liveCounts.completed} />
        <Metric label="Approved" value={liveCounts.approved} />
      </div>

      {/* Filters — horizontally scrollable on mobile so the 6 pills stay in one
          row without wrapping onto multiple crowded lines. */}
      {/* Role tabs — All / Photographer / Pilot / Employee. Sits above
          the status filter so the role slice reads as the top-level cut. */}
      <div
        className="mb-3 -mx-4 sm:mx-0"
        role="tablist"
        aria-label="Filter by user role"
        data-testid="roster-role-tabs"
      >
        <div className="flex items-center gap-2 px-4 sm:px-0 overflow-x-auto flex-nowrap scrollbar-none">
          <span className="font-mono-tag text-[10px] text-[#78584B] mr-2 shrink-0">ROLE</span>
          {[
            { key: "all",          label: "All",          icon: null },
            { key: "photographer", label: "Photographer", icon: Camera },
            { key: "pilot",        label: "Pilot",        icon: Plane },
            { key: "employee",     label: "Employee",     icon: Briefcase },
          ].map((r) => {
            const isActive = roleFilter === r.key;
            const count = roleTotals[r.key] ?? 0;
            const Icon = r.icon;
            return (
              <button
                key={r.key}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setRoleFilter(r.key)}
                data-testid={`roster-role-filter-${r.key}`}
                className={`inline-flex items-center gap-1.5 font-mono-tag text-[10px] px-3 py-1.5 rounded-sm border transition-colors whitespace-nowrap shrink-0 ${
                  isActive
                    ? "bg-[#6B0F1A] text-[#F5EBD9] border-[#6B0F1A]"
                    : "bg-transparent text-[#78584B] border-[#1A0F0A]/12 hover:text-[#6B0F1A] hover:bg-[#1A0F0A]/5"
                }`}
              >
                {Icon && <Icon className="w-3 h-3" strokeWidth={2} />}
                {r.label.toUpperCase()}
                <span
                  className={`ml-0.5 font-mono-tag text-[9px] ${
                    isActive ? "text-[#F5EBD9]/70" : "text-[#78584B]/70"
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="mb-5 -mx-4 sm:mx-0">
        <div className="flex items-center gap-2 px-4 sm:px-0 overflow-x-auto flex-nowrap sm:flex-wrap scrollbar-none">
          <span className="font-mono-tag text-[10px] text-[#78584B] mr-2 shrink-0">STATUS</span>
          {["all", ...STATUS_ORDER].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              data-testid={`filter-${s}`}
              className={`font-mono-tag text-[10px] px-3 py-1.5 rounded-sm border transition-colors whitespace-nowrap shrink-0 ${
                statusFilter === s
                  ? "bg-[#6B0F1A] text-[#F5EBD9] border-[#6B0F1A]"
                  : "bg-transparent text-[#78584B] border-[#1A0F0A]/12 hover:text-[#6B0F1A] hover:bg-[#1A0F0A]/5"
              }`}
            >
              {s === "all" ? "ALL" : STATUS_LABELS[s].label.toUpperCase()}
            </button>
          ))}

          {/* View mode toggle — pinned to the right on desktop, inline on
              mobile. Map data loads lazily the first time an admin flips to
              the map view. */}
          <div
            className="sm:ml-auto flex items-center gap-1 rounded-sm border border-[#1A0F0A]/12 p-0.5 bg-white shrink-0"
            data-testid="roster-view-toggle"
          >
            <button
              onClick={() => setViewMode("list")}
              data-testid="roster-view-list"
              className={`font-mono-tag text-[10px] px-2.5 py-1 rounded-sm inline-flex items-center gap-1.5 transition-colors ${
                viewMode === "list"
                  ? "bg-[#6B0F1A] text-[#F5EBD9]"
                  : "text-[#78584B] hover:text-[#6B0F1A]"
              }`}
              aria-pressed={viewMode === "list"}
            >
              <LayoutList className="w-3 h-3" strokeWidth={2.25} />
              LIST
            </button>
            <button
              onClick={() => setViewMode("map")}
              data-testid="roster-view-map"
              className={`font-mono-tag text-[10px] px-2.5 py-1 rounded-sm inline-flex items-center gap-1.5 transition-colors ${
                viewMode === "map"
                  ? "bg-[#6B0F1A] text-[#F5EBD9]"
                  : "text-[#78584B] hover:text-[#6B0F1A]"
              }`}
              aria-pressed={viewMode === "map"}
            >
              <MapIcon className="w-3 h-3" strokeWidth={2.25} />
              MAP
            </button>
          </div>
        </div>
      </div>

      {viewMode === "map" ? (
        <RosterMapView
          mapData={mapData}
          setMapData={setMapData}
          mapLoading={mapLoading}
          setMapLoading={setMapLoading}
          statusFilter={statusFilter}
          roleFilter={roleFilter}
        />
      ) : (
        /* Table — desktop uses a 12-col grid; mobile collapses each row into a
           stacked card because a 6-column grid on a 390px screen produces the
           overlapping/cramped columns the client reported. */
      <div className="card-surface rounded-sm overflow-hidden" data-testid="photographers-table">
        <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 border-b border-[#1A0F0A]/12 font-mono-tag text-[10px] text-[#78584B]">
          <div className="col-span-3">PHOTOGRAPHER</div>
          {roleFilter === "all" && <div className="col-span-1">ROLE</div>}
          <div className="col-span-2">STATUS</div>
          <div className="col-span-2">TIER</div>
          <div className={roleFilter === "all" ? "col-span-2" : "col-span-3"}>PROGRESS</div>
          <div className="col-span-1">JOINED</div>
          <div className="col-span-1 text-right">VIEW</div>
        </div>
        {filtered.length === 0 ? (
          (() => {
            // Distinguish "you narrowed too far" from "you haven't invited
            // anyone in this role yet". If the role tab has genuinely zero
            // users (`roleTotals[roleFilter] === 0`), show a friendly
            // Invite-first CTA that pre-selects the role in the dialog.
            const isEmptyRole =
              roleFilter !== "all" &&
              (roleTotals[roleFilter] ?? 0) === 0;
            const roleLabel = roleFilter === "pilot" ? "Pilot" : roleFilter === "employee" ? "Reelty employee" : "Photographer";
            const RoleIcon = roleFilter === "pilot" ? Plane : roleFilter === "employee" ? Briefcase : Camera;
            return isEmptyRole ? (
              <div
                className="p-12 text-center"
                data-testid={`roster-empty-${roleFilter}`}
              >
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#B08D57]/12 border border-[#B08D57]/40 mb-5">
                  <RoleIcon className="w-6 h-6 text-[#B08D57]" strokeWidth={1.75} />
                </div>
                <div className="font-display text-2xl font-extrabold text-[#1A0F0A] tracking-tight leading-tight">
                  No {roleLabel.toLowerCase()}s on the roster yet.
                </div>
                <p className="text-[#78584B] text-sm leading-relaxed mt-3 max-w-md mx-auto">
                  Send a magic-link invite and they'll land straight on the {roleLabel.toLowerCase()} onboarding checklist.
                </p>
                <Button
                  onClick={() => {
                    setInviteRole(roleFilter);
                    setInviteOpen(true);
                  }}
                  data-testid={`roster-empty-invite-${roleFilter}`}
                  className="mt-6 bg-[#6B0F1A] hover:bg-[#8A1424] text-[#F5EBD9] font-bold text-sm tracking-wide rounded-sm h-10 px-5 inline-flex items-center gap-2 btn-primary-glow"
                >
                  <UserPlus className="w-4 h-4" strokeWidth={2} />
                  Invite your first {roleLabel}
                  <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
                </Button>
              </div>
            ) : (
              <div className="p-10 text-center" data-testid="no-photographers">
                <Users className="w-8 h-8 mx-auto text-[#1A0F0A]/25 mb-3" strokeWidth={1.5} />
                <p className="text-[#78584B] text-sm">
                  {roleFilter === "all"
                    ? "No users match this filter."
                    : `No ${roleLabel.toLowerCase()}s match this status filter.`}
                </p>
              </div>
            );
          })()
        ) : (
          filtered.map((row, idx) => {
            const openDetail = () => { setSelectedId(row.user.id); loadDetail(row.user.id); };
            return (
              <motion.div
                key={row.user.id}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.02 * idx }}
                role="button"
                tabIndex={0}
                onClick={openDetail}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openDetail(); } }}
                data-testid={`photographer-row-${row.user.email}`}
                className="w-full flex flex-col gap-3 md:grid md:grid-cols-12 md:gap-4 px-4 sm:px-6 py-4 md:py-5 border-b border-[#1A0F0A]/6 hover:bg-[#6B0F1A]/[0.04] cursor-pointer transition-colors group focus:outline-none focus-visible:ring-2 focus-visible:ring-[#6B0F1A]/40"
              >
                {/* Name */}
                <div className="md:col-span-3 flex items-start justify-between gap-3 md:block min-w-0">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="text-[#1A0F0A] font-medium truncate">{row.user.name || row.user.email.split("@")[0]}</div>
                      {row.user.can_edit && (
                        <span
                          data-testid={`roster-edits-chip-${row.user.email}`}
                          title="Delivers their own edited files"
                          className="inline-flex items-center gap-1 shrink-0 font-mono-tag text-[9px] tracking-[0.16em] px-1.5 py-0.5 rounded-sm border border-[#3F7A2E]/40 bg-[#3F7A2E]/10 text-[#3F7A2E]"
                        >
                          <Sparkles className="w-2.5 h-2.5" strokeWidth={2} />
                          EDITS
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[#78584B] mt-0.5 truncate">{row.user.email}</div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-[#78584B] group-hover:text-[#6B0F1A] group-hover:translate-x-0.5 transition-all shrink-0 mt-0.5 md:hidden" strokeWidth={1.75} />
                </div>

                {/* Role — Photog / Pilot / Employee. Hidden entirely when
                    the user has narrowed to a specific role tab because
                    every row would show the same badge. Only rendered on
                    All so admins can differentiate. */}
                {roleFilter === "all" && (
                  <div className="md:col-span-1 flex items-center md:block">
                    <span className="font-mono-tag text-[9px] text-[#78584B] mr-2 md:hidden">ROLE</span>
                    <RosterRoleBadge role={row.user.role} testId={`roster-role-${row.user.email}`} />
                  </div>
                )}

                {/* Status + Tier — inline row on mobile, own columns on desktop */}
                <div className="md:col-span-2 flex items-center md:block">
                  <span className="font-mono-tag text-[9px] text-[#78584B] mr-2 md:hidden">STATUS</span>
                  <StatusBadge status={row.user.status} />
                </div>
                <div className="md:col-span-2 flex items-center md:block">
                  <span className="font-mono-tag text-[9px] text-[#78584B] mr-2 md:hidden">TIER</span>
                  <TierBadge tier={row.user.tier} />
                </div>

                {/* Progress bar — expands to col-span-3 when the ROLE
                    column is hidden so the row keeps a total of 12 cols. */}
                <div className={`${roleFilter === "all" ? "md:col-span-2" : "md:col-span-3"} flex items-center gap-3`}>
                  <span className="font-mono-tag text-[9px] text-[#78584B] md:hidden">PROGRESS</span>
                  <div className="reelty-progress-track flex-1 md:max-w-[140px]">
                    <div className="reelty-progress-fill" style={{ width: `${row.percent_complete}%` }} />
                  </div>
                  <span className="font-mono-tag text-[10px] text-[#4D3F35] shrink-0">{row.percent_complete}%</span>
                </div>

                {/* Joined date — hidden on mobile to save space; the sheet
                    detail already shows this. */}
                <div className="hidden md:flex md:col-span-1 items-center text-xs text-[#78584B] font-mono-tag">
                  {new Date(row.user.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                </div>
                <div className="hidden md:flex md:col-span-1 items-center justify-end">
                  <ChevronRight className="w-4 h-4 text-[#78584B] group-hover:text-[#6B0F1A] group-hover:translate-x-0.5 transition-all" strokeWidth={1.75} />
                </div>
              </motion.div>
            );
          })
        )}
      </div>
      )}

      {/* Detail Sheet */}
      <Sheet open={!!selectedId} onOpenChange={(open) => { if (!open) { setSelectedId(null); setSelectedDetail(null); setSelectedTimeline(null); load(); } }}>
        <SheetContent
          side="right"
          data-testid="photographer-detail-sheet"
          className="bg-[#F5EBD9] border-l border-[#1A0F0A]/12 text-[#1A0F0A] w-full sm:max-w-xl overflow-y-auto"
        >
          {!selectedDetail ? (
            <div className="min-h-[40vh] flex items-center justify-center">
              <div className="font-mono-tag text-xs text-[#78584B]">Loading…</div>
            </div>
          ) : (
            <>
              <SheetHeader className="text-left">
                <div className="font-mono-tag text-[10px] text-[#B08D57]">
                  {selectedDetail.user.role === "pilot"
                    ? "PILOT DETAIL"
                    : selectedDetail.user.role === "employee"
                    ? "EMPLOYEE DETAIL"
                    : "PHOTOGRAPHER DETAIL"}
                </div>
                <SheetTitle className="font-display text-3xl tracking-tighter text-[#1A0F0A]">
                  {selectedDetail.user.name || selectedDetail.user.email.split("@")[0]}
                </SheetTitle>
                <SheetDescription className="text-[#4D3F35]">{selectedDetail.user.email}</SheetDescription>
              </SheetHeader>

              <div className="mt-6 flex items-center gap-3 flex-wrap">
                <RosterRoleBadge role={selectedDetail.user.role} testId="drawer-role-badge" />
                <StatusBadge status={selectedDetail.user.status} />
                <TierBadge tier={selectedDetail.user.tier} showRate />
                <div className="font-mono-tag text-[10px] text-[#78584B]">
                  {selectedDetail.percent_complete}% complete · {selectedDetail.completed_items}/{selectedDetail.total_items} docs
                </div>
              </div>

              {/* APPLICANT FACT SHEET — every piece of info collected during
                  the HR / applicant phase carries onto the User record at
                  invite time (see server.invite_applicant). Editable
                  in-place so admins never need to leave the drawer. */}
              <ApplicantFactSheet
                user={selectedDetail.user}
                onSave={saveFactSheet}
              />

              {/* Progress timeline — invited → started → completed → approved → handoff */}
              <div className="mt-6">
                <PhotographerTimeline
                  milestones={selectedTimeline || []}
                  loading={selectedTimeline === null}
                />
              </div>

              {/* Shoots Hub handoff status (only meaningful when approved) */}
              {selectedDetail.user.status === "approved" && (
                <div className="mt-6 card-surface rounded-sm p-5" data-testid="handoff-status-card">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                    <div className="flex items-center gap-2">
                      <Radio className="w-4 h-4 text-[#6B0F1A]" strokeWidth={1.75} />
                      <div className="font-mono-tag text-[10px] text-[#78584B]">REELTY PROPERTY SHOOTS HUB</div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        onClick={testHubConnection}
                        disabled={hubTesting}
                        data-testid="hub-test-connection-btn"
                        variant="ghost"
                        size="sm"
                        className="text-[#1A0F0A] hover:bg-[#1A0F0A]/6 font-mono-tag text-[10px] border border-[#1A0F0A]/20"
                        title="Send a signed photographer.test ping to the Hub"
                      >
                        <Zap className="w-3 h-3 mr-1.5" strokeWidth={2} />
                        {hubTesting ? "Testing…" : "Test connection"}
                      </Button>
                      <Button
                        onClick={loadHubPreview}
                        disabled={hubPreviewLoading}
                        data-testid="hub-preview-payload-btn"
                        variant="ghost"
                        size="sm"
                        className="text-[#1A0F0A] hover:bg-[#1A0F0A]/6 font-mono-tag text-[10px] border border-[#1A0F0A]/20"
                        title="Show the exact payload + signed headers we'd send"
                      >
                        <Eye className="w-3 h-3 mr-1.5" strokeWidth={2} />
                        {hubPreviewLoading ? "Loading…" : (hubPreviewOpen ? "Hide payload" : "Preview payload")}
                      </Button>
                      <Button
                        onClick={retryHandoff}
                        disabled={retryingHandoff}
                        data-testid="handoff-send-btn"
                        variant="ghost"
                        size="sm"
                        className="text-[#6B0F1A] hover:bg-[#6B0F1A]/8 font-mono-tag text-[10px] border border-[#6B0F1A]/30"
                      >
                        <Send className="w-3 h-3 mr-1.5" strokeWidth={2} />
                        {retryingHandoff ? "Sending…" : (selectedHandoff ? "Resend" : "Send handoff")}
                      </Button>
                    </div>
                  </div>

                  {/* Test-connection result (independent of handoff attempts) */}
                  {hubTestResult && (
                    <div
                      className="mb-3 p-3 rounded-sm border border-[#1A0F0A]/10 bg-[#1A0F0A]/3"
                      data-testid="hub-test-result"
                    >
                      <div className="flex items-center gap-2 flex-wrap">
                        {hubTestResult.status === "success" && (
                          <span className="font-mono-tag text-[10px] text-[#B08D57] inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm border border-[#B08D57]/50 bg-[#B08D57]/12">
                            <CheckCircle2 className="w-3 h-3" strokeWidth={2} />
                            CONNECTION OK · HTTP {hubTestResult.response_code}
                          </span>
                        )}
                        {hubTestResult.status === "failed" && (
                          <span className="font-mono-tag text-[10px] text-[#6B0F1A] inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm border border-[#6B0F1A]/40 bg-[#6B0F1A]/10">
                            <AlertTriangle className="w-3 h-3" strokeWidth={2} />
                            TEST FAILED{hubTestResult.response_code ? ` · HTTP ${hubTestResult.response_code}` : ""}
                          </span>
                        )}
                        {hubTestResult.status === "pending_config" && (
                          <span className="font-mono-tag text-[10px] text-[#78584B] inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm border border-[#1A0F0A]/12 bg-[#1A0F0A]/6">
                            <AlertTriangle className="w-3 h-3" strokeWidth={2} />
                            HUB URL MISSING
                          </span>
                        )}
                        {hubTestResult.hub_url_masked && (
                          <span className="font-mono-tag text-[9px] text-[#78584B] truncate max-w-full">
                            → {hubTestResult.hub_url_masked}
                          </span>
                        )}
                      </div>
                      {hubTestResult.error && (
                        <div className="text-[11px] text-[#6B0F1A] leading-relaxed mt-2">
                          {hubTestResult.error}
                        </div>
                      )}
                      {hubTestResult.response_body && (
                        <pre
                          className="mt-2 text-[10px] leading-snug text-[#1A0F0A] bg-white/70 border border-[#1A0F0A]/10 rounded-sm p-2 overflow-x-auto max-h-32"
                          data-testid="hub-test-result-body"
                        >
                          {hubTestResult.response_body}
                        </pre>
                      )}
                    </div>
                  )}

                  {/* Dry-run payload preview */}
                  {hubPreviewOpen && hubPreview && (
                    <div
                      className="mb-3 p-3 rounded-sm border border-[#B08D57]/30 bg-[#B08D57]/6"
                      data-testid="hub-payload-preview"
                    >
                      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                        <div className="font-mono-tag text-[10px] text-[#78584B]">
                          {hubPreview.payload_type.toUpperCase()} · DRY RUN — NOTHING SENT
                        </div>
                        {hubPreview.target_url_host && (
                          <div className="font-mono-tag text-[9px] text-[#78584B]">
                            → {hubPreview.target_url_host}
                          </div>
                        )}
                      </div>
                      <div className="font-mono-tag text-[9px] text-[#78584B] mb-1">HEADERS</div>
                      <pre className="text-[10px] leading-snug text-[#1A0F0A] bg-white/70 border border-[#1A0F0A]/10 rounded-sm p-2 overflow-x-auto">
{Object.entries(hubPreview.headers).map(([k, v]) => `${k}: ${v}`).join("\n")}
                      </pre>
                      <div className="font-mono-tag text-[9px] text-[#78584B] mt-2 mb-1">BODY</div>
                      <pre className="text-[10px] leading-snug text-[#1A0F0A] bg-white/70 border border-[#1A0F0A]/10 rounded-sm p-2 overflow-x-auto max-h-56">
{JSON.stringify(hubPreview.payload, null, 2)}
                      </pre>
                      {!hubPreview.hub_secret_configured && (
                        <div className="text-[11px] text-[#78584B] leading-relaxed mt-2">
                          Signature placeholder shown — set <code className="text-[#6B0F1A]">SHOOTS_HUB_WEBHOOK_SECRET</code> to generate a real HMAC.
                        </div>
                      )}
                    </div>
                  )}

                  {!selectedHandoff && (
                    <div className="text-xs text-[#78584B] leading-relaxed">
                      No handoff attempts yet. Click Send to POST this photographer's profile to the Shoots Hub.
                    </div>
                  )}
                  {selectedHandoff && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        {selectedHandoff.status === "success" && (
                          <span className="font-mono-tag text-[10px] text-[#B08D57] inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm border border-[#B08D57]/50 bg-[#B08D57]/12">
                            <CheckCircle2 className="w-3 h-3" strokeWidth={2} />
                            SENT · HTTP {selectedHandoff.response_code}
                          </span>
                        )}
                        {selectedHandoff.status === "failed" && (
                          <span className="font-mono-tag text-[10px] text-[#6B0F1A] inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm border border-[#6B0F1A]/40 bg-[#6B0F1A]/10">
                            <AlertTriangle className="w-3 h-3" strokeWidth={2} />
                            FAILED{selectedHandoff.response_code ? ` · HTTP ${selectedHandoff.response_code}` : ""}
                          </span>
                        )}
                        {selectedHandoff.status === "pending_config" && (
                          <span className="font-mono-tag text-[10px] text-[#78584B] inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm border border-[#1A0F0A]/12 bg-[#1A0F0A]/6">
                            <AlertTriangle className="w-3 h-3" strokeWidth={2} />
                            AWAITING HUB CONFIG
                          </span>
                        )}
                        <span className="font-mono-tag text-[9px] text-[#78584B]">
                          {selectedHandoff.last_attempt_at
                            ? new Date(selectedHandoff.last_attempt_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
                            : ""}
                        </span>
                      </div>
                      {selectedHandoff.error && (
                        <div className="text-[11px] text-[#6B0F1A] leading-relaxed bg-[#6B0F1A]/6 border border-[#6B0F1A]/20 rounded-sm p-2">
                          {selectedHandoff.error}
                        </div>
                      )}
                      {!selectedHandoff.hub_url_configured && (
                        <div className="text-[11px] text-[#78584B] leading-relaxed">
                          Set <code className="text-[#6B0F1A]">SHOOTS_HUB_WEBHOOK_URL</code> in the backend env to the external Reelty Shoots Hub endpoint. The webhook is signed with <code className="text-[#6B0F1A]">SHOOTS_HUB_WEBHOOK_SECRET</code> (Bearer + HMAC-SHA256).
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Final Exam score summary — always rendered so admins can
                  spot who's still owed the exam. Score presence dictates
                  the pass/fail badge. Kept above the Tier setter so it
                  reads as part of the "outcome" section (badge → tier). */}
              {(() => {
                const u = selectedDetail.user;
                const attempts = u.exam_attempts_count || 0;
                const score = u.exam_last_score;
                const passedAt = u.exam_last_passed_at;
                const takenAt = u.exam_last_taken_at;
                const passed = score != null && score >= 80;
                const fmt = (iso) =>
                  new Date(iso).toLocaleDateString(undefined, {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  });
                return (
                  <div
                    className="mt-6 card-surface rounded-sm p-5"
                    data-testid="admin-exam-card"
                  >
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <div className="flex items-center gap-2">
                        <GraduationCap
                          className="w-4 h-4 text-[#B08D57]"
                          strokeWidth={1.75}
                        />
                        <div className="font-mono-tag text-[10px] text-[#78584B]">
                          FINAL EXAM
                        </div>
                      </div>
                      {attempts > 0 && (
                        <div
                          className="font-mono-tag text-[9px] text-[#78584B]"
                          data-testid="admin-exam-attempts"
                        >
                          {attempts} {attempts === 1 ? "ATTEMPT" : "ATTEMPTS"}
                        </div>
                      )}
                    </div>

                    {attempts === 0 ? (
                      <div className="text-xs text-[#78584B] leading-relaxed">
                        Not yet attempted. Passing requires a score of 80% or
                        higher on Reelty's scenario-based exam.
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="flex items-center gap-3 flex-wrap">
                          <div
                            className={`inline-flex items-baseline gap-1.5 px-3 py-1.5 rounded-sm border ${
                              passed
                                ? "border-[#3F7A2E]/50 bg-[#3F7A2E]/10 text-[#3F7A2E]"
                                : "border-[#6B0F1A]/40 bg-[#6B0F1A]/10 text-[#6B0F1A]"
                            }`}
                            data-testid="admin-exam-score-badge"
                          >
                            <span className="text-2xl font-bold leading-none">
                              {score}
                            </span>
                            <span className="font-mono-tag text-[10px]">%</span>
                          </div>
                          <span
                            className={`font-mono-tag text-[10px] inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm border ${
                              passed
                                ? "border-[#3F7A2E]/50 bg-[#3F7A2E]/10 text-[#3F7A2E]"
                                : "border-[#6B0F1A]/40 bg-[#6B0F1A]/10 text-[#6B0F1A]"
                            }`}
                          >
                            {passed ? (
                              <>
                                <CheckCircle2 className="w-3 h-3" strokeWidth={2} />
                                PASSED
                              </>
                            ) : (
                              <>
                                <AlertTriangle className="w-3 h-3" strokeWidth={2} />
                                BELOW PASSING · NEEDS 80%+
                              </>
                            )}
                          </span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px] text-[#78584B]">
                          {takenAt && (
                            <div>
                              <span className="font-mono-tag text-[9px] text-[#78584B]">
                                LAST TAKEN
                              </span>
                              <div className="text-[#1A0F0A] mt-0.5">
                                {fmt(takenAt)}
                              </div>
                            </div>
                          )}
                          {passedAt && (
                            <div>
                              <span className="font-mono-tag text-[9px] text-[#78584B]">
                                FIRST PASSED
                              </span>
                              <div className="text-[#3F7A2E] mt-0.5">
                                {fmt(passedAt)}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Editing capability toggle — flips whether this creator
                  handles their own post-production. Yes/No pill pair;
                  optimistic update via toggleCanEdit. */}
              {(() => {
                const canEdit = !!selectedDetail.user.can_edit;
                return (
                  <div
                    className="mt-6 card-surface rounded-sm p-5"
                    data-testid="admin-can-edit-card"
                  >
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <div className="font-mono-tag text-[10px] text-[#78584B] mb-1">
                          POST-PRODUCTION
                        </div>
                        <div className="font-display font-bold text-[#1A0F0A]">
                          Does their own editing?
                        </div>
                        <p className="text-xs text-[#78584B] mt-1 leading-relaxed max-w-md">
                          Default is <b>No</b> — Reelty's editors handle post. Toggle to <b>Yes</b> when this creator delivers final files themselves.
                        </p>
                      </div>
                      <div className="inline-flex rounded-sm border border-[#1A0F0A]/12 overflow-hidden shrink-0" role="radiogroup">
                        <button
                          type="button"
                          role="radio"
                          aria-checked={canEdit}
                          onClick={() => !canEdit && toggleCanEdit(selectedDetail.user.id, true)}
                          data-testid="can-edit-yes"
                          className={`px-4 h-9 font-mono-tag text-[10px] tracking-[0.16em] transition-colors ${
                            canEdit
                              ? "bg-[#3F7A2E] text-[#F5EBD9]"
                              : "bg-white text-[#78584B] hover:bg-[#1A0F0A]/5"
                          }`}
                        >
                          YES
                        </button>
                        <button
                          type="button"
                          role="radio"
                          aria-checked={!canEdit}
                          onClick={() => canEdit && toggleCanEdit(selectedDetail.user.id, false)}
                          data-testid="can-edit-no"
                          className={`px-4 h-9 font-mono-tag text-[10px] tracking-[0.16em] transition-colors border-l border-[#1A0F0A]/12 ${
                            !canEdit
                              ? "bg-[#6B0F1A] text-[#F5EBD9]"
                              : "bg-white text-[#78584B] hover:bg-[#1A0F0A]/5"
                          }`}
                        >
                          NO
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Tier setter */}
              <div className="mt-8 card-surface rounded-sm p-5" data-testid="tier-setter">
                <div className="font-mono-tag text-[10px] text-[#78584B] mb-3">PHOTOGRAPHER TIER · PAYS PER SHOOT</div>
                <div className="grid grid-cols-3 gap-2">
                  {TIER_ORDER.map((t) => {
                    const meta = TIER_META[t];
                    const Icon = meta.icon;
                    const selected = selectedDetail.user.tier === t;
                    return (
                      <button
                        key={t}
                        onClick={() => updateTier(selectedDetail.user.id, t)}
                        data-testid={`tier-option-${t}`}
                        className={`p-3 rounded-sm border transition-all text-left ${
                          selected
                            ? "border-[color:var(--tier-c)]"
                            : "border-[#1A0F0A]/10 bg-white/40 hover:border-[#1A0F0A]/25"
                        }`}
                        style={selected ? { "--tier-c": meta.color, backgroundColor: `${meta.color}14` } : { "--tier-c": meta.color }}
                      >
                        <Icon
                          className="w-4 h-4 mb-2"
                          strokeWidth={2}
                          style={{ color: selected ? (meta.color === "#B08D57" ? "#B08D57" : meta.color) : "#78584B" }}
                        />
                        <div className={`font-display text-sm font-bold ${selected ? "text-[#1A0F0A]" : "text-[#4D3F35]"}`}>
                          {meta.label}
                        </div>
                        <div className="font-mono-tag text-[9px] text-[#78584B] mt-1">${meta.rate}/SHOOT</div>
                      </button>
                    );
                  })}
                </div>
                {selectedDetail.user.tier && (
                  <button
                    onClick={() => updateTier(selectedDetail.user.id, "unassigned")}
                    data-testid="tier-clear-btn"
                    className="mt-3 font-mono-tag text-[10px] text-[#78584B] hover:text-[#6B0F1A]"
                  >
                    ← CLEAR TIER
                  </button>
                )}
                <p className="text-[11px] text-[#78584B] mt-3 leading-relaxed">
                  Tier can be set before, during, or after onboarding. It determines the photographer's per-shoot rate.
                </p>
              </div>

              {/* Status setter */}
              <div className="mt-6 card-surface rounded-sm p-5" data-testid="status-setter">
                <div className="font-mono-tag text-[10px] text-[#78584B] mb-3">SET STATUS</div>
                <Select
                  value={selectedDetail.user.status}
                  onValueChange={(v) => updateStatus(selectedDetail.user.id, v)}
                  disabled={savingStatus}
                >
                  <SelectTrigger data-testid="status-select-trigger" className="bg-white border-[#1A0F0A]/15 text-[#1A0F0A] rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#FBF4E4] border-[#1A0F0A]/12 text-[#1A0F0A]">
                    {STATUS_ORDER.map((s) => (
                      <SelectItem key={s} value={s} data-testid={`status-option-${s}`}>
                        {STATUS_LABELS[s].label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-[#78584B] mt-3 leading-relaxed">
                  Approve a photographer once their onboarding is verified. Approving unlocks property shoot assignments.
                </p>
                {selectedDetail.user.status !== "approved" && selectedDetail.percent_complete === 100 && (
                  <Button
                    onClick={() => updateStatus(selectedDetail.user.id, "approved")}
                    disabled={savingStatus}
                    data-testid="approve-btn"
                    className="mt-4 w-full bg-[#B08D57] hover:bg-[#9C7A45] text-[#1A0F0A] font-bold text-xs tracking-wide rounded-sm"
                  >
                    <Sparkles className="w-4 h-4 mr-2" strokeWidth={2} />
                    Approve for shoots
                  </Button>
                )}
              </div>

              {/* Service area (editable) */}
              <div className="mt-6 card-surface rounded-sm p-5" data-testid="detail-service-area">
                <div className="flex items-center justify-between mb-3">
                  <div className="font-mono-tag text-[10px] text-[#78584B]">PROFILE & SERVICE AREA</div>
                  {!editingProfile && (
                    <Button
                      onClick={openEditProfile}
                      data-testid="edit-profile-btn"
                      variant="ghost"
                      size="sm"
                      className="text-[#6B0F1A] hover:bg-[#6B0F1A]/8 font-mono-tag text-[10px] border border-[#6B0F1A]/30 h-7 px-2"
                    >
                      <Pencil className="w-3 h-3 mr-1.5" strokeWidth={2} />
                      Edit
                    </Button>
                  )}
                </div>

                {!editingProfile ? (
                  <div className="space-y-3">
                    <div>
                      <div className="font-mono-tag text-[9px] text-[#78584B]">NAME</div>
                      <div className="font-display text-base font-semibold mt-0.5 text-[#1A0F0A]">
                        {selectedDetail.user.name || <span className="text-[#78584B] italic">Not set</span>}
                      </div>
                    </div>
                    {selectedDetail.user.base_zip_code ? (
                      <div className="flex items-center gap-6">
                        <div>
                          <div className="font-mono-tag text-[9px] text-[#78584B]">BASE ZIP</div>
                          <div className="font-display text-xl font-bold mt-0.5 text-[#1A0F0A]">{selectedDetail.user.base_zip_code}</div>
                        </div>
                        <div>
                          <div className="font-mono-tag text-[9px] text-[#78584B]">RADIUS</div>
                          <div className="font-display text-xl font-bold mt-0.5 text-[#1A0F0A]">
                            {selectedDetail.user.driving_radius_miles} <span className="text-[#78584B] text-sm">mi</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-[#78584B]">Service area not yet submitted.</div>
                    )}
                  </div>
                ) : (
                  <form onSubmit={saveProfile} className="space-y-3" data-testid="edit-profile-form">
                    <div>
                      <Label className="font-mono-tag text-[10px] text-[#78584B]">NAME</Label>
                      <Input
                        data-testid="edit-name-input"
                        value={editForm.name}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                        placeholder="Alex Rivera"
                        className="mt-1.5 bg-white border-[#1A0F0A]/15 focus-visible:border-[#6B0F1A] focus-visible:ring-[#6B0F1A]/25 rounded-sm text-[#1A0F0A]"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="font-mono-tag text-[10px] text-[#78584B]">BASE ZIP</Label>
                        <Input
                          data-testid="edit-zip-input"
                          value={editForm.base_zip_code}
                          onChange={(e) => setEditForm({ ...editForm, base_zip_code: e.target.value })}
                          placeholder="10001"
                          inputMode="numeric"
                          className="mt-1.5 bg-white border-[#1A0F0A]/15 focus-visible:border-[#6B0F1A] focus-visible:ring-[#6B0F1A]/25 rounded-sm text-[#1A0F0A]"
                        />
                      </div>
                      <div>
                        <Label className="font-mono-tag text-[10px] text-[#78584B]">RADIUS (MI)</Label>
                        <Input
                          data-testid="edit-radius-input"
                          type="number"
                          min={1}
                          max={500}
                          value={editForm.driving_radius_miles}
                          onChange={(e) => setEditForm({ ...editForm, driving_radius_miles: e.target.value })}
                          placeholder="25"
                          className="mt-1.5 bg-white border-[#1A0F0A]/15 focus-visible:border-[#6B0F1A] focus-visible:ring-[#6B0F1A]/25 rounded-sm text-[#1A0F0A]"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2 pt-1">
                      <Button
                        type="submit"
                        data-testid="save-profile-btn"
                        disabled={savingProfile}
                        className="bg-[#6B0F1A] hover:bg-[#8A1424] text-[#F5EBD9] font-bold text-xs tracking-wide rounded-sm flex-1"
                      >
                        <Save className="w-3.5 h-3.5 mr-1.5" strokeWidth={2} />
                        {savingProfile ? "Saving…" : "Save changes"}
                      </Button>
                      <Button
                        type="button"
                        onClick={() => setEditingProfile(false)}
                        data-testid="cancel-profile-btn"
                        variant="ghost"
                        className="text-[#78584B] hover:bg-[#1A0F0A]/5 font-mono-tag text-[10px]"
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                )}
              </div>

              {/* Checklist (toggleable by admin on the photographer's behalf).
                  Grouped by phase with sub-headers so the flow reads exactly
                  as the photographer sees it on their own dashboard.
                  Defensive sort by `order` inside each group in case any
                  legacy rows arrive with the field missing. */}
              <div className="mt-6" data-testid="detail-checklist">
                <div className="flex items-baseline justify-between mb-3">
                  <div className="font-mono-tag text-[10px] text-[#78584B]">CHECKLIST · CLICK TO TOGGLE</div>
                  <div className="font-mono-tag text-[10px] text-[#78584B]">
                    {selectedDetail.completed_items}/{selectedDetail.total_items}
                  </div>
                </div>
                {(() => {
                  const PHASE_ORDER = [
                    { key: "documents",    label: "Documents" },
                    { key: "tax_payments", label: "Tax & Payments" },
                    { key: "exam",         label: "Final Exam" },
                    { key: "scheduling",   label: "Scheduling" },
                  ];
                  const byPhase = new Map(PHASE_ORDER.map((p) => [p.key, []]));
                  for (const it of selectedDetail.items) {
                    if (!byPhase.has(it.phase)) byPhase.set(it.phase, []);
                    byPhase.get(it.phase).push(it);
                  }
                  // Sort inside each phase by `order` (fallback 0), then title
                  // so the display order matches the photographer's dashboard.
                  for (const [, list] of byPhase) {
                    list.sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.title.localeCompare(b.title));
                  }
                  return (
                    <div className="space-y-5">
                      {PHASE_ORDER.map((phase) => {
                        const items = byPhase.get(phase.key) || [];
                        if (items.length === 0) return null;
                        const doneInPhase = items.filter((i) => i.completed).length;
                        return (
                          <div key={phase.key} data-testid={`admin-checklist-phase-${phase.key}`}>
                            <div className="flex items-baseline justify-between mb-2">
                              <div className="font-mono-tag text-[10px] tracking-[0.2em] text-[#B08D57]">
                                {phase.label.toUpperCase()}
                              </div>
                              <div className="font-mono-tag text-[9px] text-[#78584B]">
                                {doneInPhase}/{items.length}
                              </div>
                            </div>
                            <div className="space-y-2">
                              {items.map((it) => (
                                <div
                                  key={it.id}
                                  data-testid={`admin-checklist-item-${it.id}`}
                                  className={`card-surface rounded-sm p-4 transition-all ${
                                    togglingItem === it.id ? "opacity-60" : "hover:border-[#6B0F1A]/40"
                                  }`}
                                >
                      <label className="flex items-start gap-3 cursor-pointer">
                        <Checkbox
                          checked={it.completed}
                          data-testid={`admin-toggle-${it.id}`}
                          disabled={togglingItem === it.id}
                          onCheckedChange={(v) => toggleAdminItem(it.id, Boolean(v))}
                          className="mt-0.5 w-4 h-4 border-[#1A0F0A]/25 data-[state=checked]:bg-[#6B0F1A] data-[state=checked]:border-[#6B0F1A] data-[state=checked]:text-[#F5EBD9] rounded-sm"
                        />
                        <div className="flex-1 min-w-0">
                          <div className={`text-sm font-medium ${it.completed ? "text-[#78584B] line-through" : "text-[#1A0F0A]"}`}>
                            {it.title}
                          </div>
                          <div className="font-mono-tag text-[9px] text-[#78584B] mt-1">
                            {it.phase.replace("_", " ").toUpperCase()} · {it.action_type.toUpperCase()}
                            {it.completed_at && ` · Completed ${new Date(it.completed_at).toLocaleDateString()}`}
                          </div>
                        </div>
                      </label>
                      {(it.attachments || []).map((att) => (
                        <div
                          key={att.id}
                          className="mt-3 ml-7 flex items-center gap-3 p-2.5 rounded-sm border border-[#B08D57]/40 bg-[#B08D57]/8"
                          data-testid={`admin-checklist-attachment-${it.id}-${att.id}`}
                        >
                          <FileText className="w-3.5 h-3.5 text-[#B08D57] flex-shrink-0" strokeWidth={1.75} />
                          <div className="flex-1 min-w-0">
                            <div className="text-[11px] font-medium text-[#1A0F0A] truncate" title={att.filename}>
                              {att.filename}
                            </div>
                            <div className="font-mono-tag text-[9px] text-[#78584B] mt-0.5">
                              {formatBytes(att.size)} · UPLOADED {new Date(att.uploaded_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => setPreviewAttachment({
                              userId: selectedDetail.user.id,
                              itemId: it.id,
                              fileId: att.id,
                              filename: att.filename,
                              contentType: att.content_type,
                              size: att.size,
                              uploadedAt: att.uploaded_at,
                            })}
                            data-testid={`admin-preview-attachment-${it.id}-${att.id}`}
                            className="font-mono-tag text-[10px] text-[#B08D57] hover:underline"
                          >
                            PREVIEW
                          </button>
                          <button
                            type="button"
                            onClick={() => openAdminAttachment(selectedDetail.user.id, it.id, att.id, true)}
                            data-testid={`admin-download-attachment-${it.id}-${att.id}`}
                            className="font-mono-tag text-[10px] text-[#6B0F1A] hover:underline"
                          >
                            DOWNLOAD
                          </button>
                        </div>
                      ))}
                    </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}

                {/* Download-all footer — only shown when at least one file exists across all items */}
                {selectedDetail.items.some((it) => (it.attachments || []).length > 0) && (
                  <div className="mt-4" data-testid="detail-download-all-wrap">
                    {(() => {
                      const totalFiles = selectedDetail.items.reduce(
                        (sum, it) => sum + ((it.attachments || []).length),
                        0
                      );
                      return (
                        <>
                          <Button
                            onClick={() => downloadAllDocs(
                              selectedDetail.user.id,
                              selectedDetail.user.name || selectedDetail.user.email
                            )}
                            data-testid="detail-download-all-btn"
                            className="w-full bg-[#6B0F1A] hover:bg-[#8A1424] text-[#F5EBD9] font-bold text-xs tracking-wide rounded-sm btn-primary-glow inline-flex items-center justify-center gap-2"
                          >
                            <Archive className="w-3.5 h-3.5" strokeWidth={2.25} />
                            Download all documents ({totalFiles} {totalFiles === 1 ? "file" : "files"})
                          </Button>
                          <p className="font-mono-tag text-[9px] text-[#78584B] mt-2 text-center">
                            BUNDLES EVERY UPLOAD INTO A SINGLE .ZIP WITH A MANIFEST
                          </p>
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>

              <div className="mt-8 pt-6 border-t border-[#1A0F0A]/10 flex flex-col sm:flex-row gap-3 sm:items-center">
                <Button
                  onClick={() => resendInvite(selectedDetail.user.id)}
                  data-testid="resend-invite-btn"
                  variant="ghost"
                  className="w-full sm:w-auto justify-center border border-[#1A0F0A]/12 hover:bg-[#1A0F0A]/5 text-[#1A0F0A] font-mono-tag text-[10px]"
                >
                  <RefreshCw className="w-3.5 h-3.5 mr-2" strokeWidth={2} />
                  Resend magic link
                </Button>
                <Button
                  onClick={deletePhotographer}
                  data-testid="delete-photographer-btn"
                  variant="ghost"
                  className="w-full sm:w-auto sm:ml-auto justify-center border border-[#6B0F1A]/30 hover:bg-[#6B0F1A]/8 text-[#6B0F1A] font-mono-tag text-[10px]"
                >
                  <Trash2 className="w-3.5 h-3.5 mr-2" strokeWidth={2} />
                  Delete photographer
                </Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Manage Admins dialog */}
      <Dialog open={adminsOpen} onOpenChange={setAdminsOpen}>
        <DialogContent
          className="bg-[#FBF4E4] border-[#1A0F0A]/12 text-[#1A0F0A] rounded-sm max-w-lg"
          data-testid="manage-admins-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight text-[#1A0F0A] flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-[#6B0F1A]" strokeWidth={2} />
              Manage admins
            </DialogTitle>
            <DialogDescription className="text-[#4D3F35]">
              Grant a teammate the same admin access you have. They'll receive a magic-link email.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={inviteAdmin} className="space-y-3 mt-2" data-testid="invite-admin-form">
            <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
              <div>
                <Label htmlFor="admin-invite-email" className="font-mono-tag text-[10px] text-[#78584B]">
                  EMAIL
                </Label>
                <Input
                  id="admin-invite-email"
                  data-testid="admin-invite-email-input"
                  type="email"
                  required
                  value={adminInviteEmail}
                  onChange={(e) => setAdminInviteEmail(e.target.value)}
                  placeholder="teammate@reelty.com"
                  className="mt-1.5 bg-white border-[#1A0F0A]/15 focus-visible:border-[#6B0F1A] focus-visible:ring-[#6B0F1A]/25 rounded-sm text-[#1A0F0A]"
                />
              </div>
              <div>
                <Label htmlFor="admin-invite-name" className="font-mono-tag text-[10px] text-[#78584B]">
                  NAME (OPTIONAL)
                </Label>
                <Input
                  id="admin-invite-name"
                  data-testid="admin-invite-name-input"
                  value={adminInviteName}
                  onChange={(e) => setAdminInviteName(e.target.value)}
                  placeholder="Casey Kim"
                  className="mt-1.5 bg-white border-[#1A0F0A]/15 focus-visible:border-[#6B0F1A] focus-visible:ring-[#6B0F1A]/25 rounded-sm text-[#1A0F0A]"
                />
              </div>
              <Button
                type="submit"
                data-testid="admin-invite-send-btn"
                disabled={invitingAdmin}
                className="bg-[#6B0F1A] hover:bg-[#8A1424] text-[#F5EBD9] font-bold tracking-wide rounded-sm"
              >
                <Send className="w-4 h-4 mr-1.5" strokeWidth={2} />
                {invitingAdmin ? "Sending…" : "Invite"}
              </Button>
            </div>
          </form>

          <div className="mt-5">
            <div className="font-mono-tag text-[10px] text-[#78584B] mb-2">CURRENT ADMINS</div>
            {adminsLoading ? (
              <div className="text-xs text-[#78584B] py-3">Loading…</div>
            ) : admins.length === 0 ? (
              <div className="text-xs text-[#78584B] py-3">No admins found.</div>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto pr-1" data-testid="admins-list">
                {admins.map((a) => {
                  const isSelf = currentUser?.id === a.id;
                  const busy = revokingAdminId === a.id;
                  return (
                    <div
                      key={a.id}
                      data-testid={`admin-row-${a.email}`}
                      className="card-surface rounded-sm p-3 flex items-center gap-3"
                    >
                      <ShieldCheck className="w-4 h-4 text-[#B08D57]" strokeWidth={1.75} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-[#1A0F0A] truncate">
                          {a.name || a.email.split("@")[0]}
                          {isSelf && (
                            <span className="ml-2 font-mono-tag text-[9px] text-[#6B0F1A] align-middle">
                              YOU
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-[#78584B] truncate">{a.email}</div>
                      </div>
                      <Button
                        onClick={() => resendAdminInvite(a.id)}
                        data-testid={`admin-resend-btn-${a.email}`}
                        variant="ghost"
                        size="sm"
                        className="text-[#4D3F35] hover:bg-[#1A0F0A]/5 font-mono-tag text-[10px] h-8 px-2"
                      >
                        <RefreshCw className="w-3 h-3 mr-1" strokeWidth={2} />
                        Link
                      </Button>
                      {!isSelf && (
                        <Button
                          onClick={() => revokeAdmin(a.id, a.email)}
                          data-testid={`admin-revoke-btn-${a.email}`}
                          disabled={busy}
                          variant="ghost"
                          size="sm"
                          className="text-[#6B0F1A] hover:bg-[#6B0F1A]/8 font-mono-tag text-[10px] h-8 px-2 border border-[#6B0F1A]/25"
                        >
                          <Trash2 className="w-3 h-3 mr-1" strokeWidth={2} />
                          {busy ? "…" : "Revoke"}
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* In-app PDF/image preview modal — keeps admin review inside the roster drawer */}
      <AttachmentPreviewModal
        open={!!previewAttachment}
        onClose={() => setPreviewAttachment(null)}
        userId={previewAttachment?.userId}
        itemId={previewAttachment?.itemId}
        fileId={previewAttachment?.fileId}
        filename={previewAttachment?.filename}
        contentType={previewAttachment?.contentType}
        size={previewAttachment?.size}
        uploadedAt={previewAttachment?.uploadedAt}
      />
    </div>
  );
}
